import { toast } from "react-toastify";
import { FiCopy, FiExternalLink } from "react-icons/fi";

import { googleMapsEmbedUrl, googleMapsSearchUrl } from "src/Common/googleMaps";

/**
 * Small embedded Google map for an address. Renders nothing without an
 * address or an API key. Pass the height through `className` (e.g. "h-[160px]").
 */
export default function AddressMap({ address, className = "h-[160px]" }) {
  const src = address ? googleMapsEmbedUrl(address) : null;
  if (!src) return null;

  return (
    <iframe
      title={`Map of ${address}`}
      src={src}
      loading="lazy"
      allowFullScreen
      // Google's recommended policy: sends only our origin, which is enough
      // for the key's HTTP referrer restriction.
      referrerPolicy="strict-origin-when-cross-origin"
      className={`block w-full rounded-[6px] border border-borderColor ${className}`}
    />
  );
}

/** "Open in Google Maps" + "Copy" links shown under a saved address. */
export function AddressActions({ address }) {
  if (!address) return null;

  const copy = () => {
    if (!navigator.clipboard) {
      toast.error("Could not copy to clipboard");
      return;
    }
    navigator.clipboard
      .writeText(address)
      .then(() => toast.success("Address copied"))
      .catch(() => toast.error("Could not copy to clipboard"));
  };

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 normal-case">
      <a
        href={googleMapsSearchUrl(address)}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 text-[12px] leading-[18px] tracking-[0.12px] font-OpenSansSemiBold text-blueColor"
      >
        <FiExternalLink size={12} /> Open in Google Maps
      </a>
      <button
        type="button"
        onClick={copy}
        className="inline-flex items-center gap-1 text-[12px] leading-[18px] tracking-[0.12px] font-OpenSansSemiBold text-subTitleColor"
      >
        <FiCopy size={12} /> Copy
      </button>
    </div>
  );
}
